import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Undo2, X } from 'lucide-react';

import { IMPRESSION_SUIVI_LOTS, lotsAPI } from '../../lib/lotsApi';
import { RETOURS_LOT, peutEtreRendu, peutPartirEnRetour } from '../../lib/lotsFiltres';
import { indexerLignes, resumerRetour } from '../../lib/lotsResultats';
import { formatDate } from '../../lib/formats';
import { libelle } from '../../lib/libelles';
import { messageErreur } from '../../lib/erreurs';
import { cn } from '../../lib/utils';
import { toast } from '../../components/ui/sonner';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { useTransporteursLots } from '../../hooks/useTransporteursLots';
import { PageHeader } from '../../components/ui/page-header';
import { PrintMenu } from '../../components/business/PrintMenu';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { EmptyState } from '../../components/ui/empty-state';
import { LoadingState } from '../../components/ui/loading-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import RetourDialog, { ACTION_DEPART_RETOUR, ACTION_RENDU } from '../../components/lots/RetourDialog';
import {
  BoutonEnvoiResponsable,
  COULEURS_RETOUR,
  ChampRecherche,
  ChauffeurLigne,
  LienLot,
  LienTrajet,
  PanneauResultat,
} from '../../components/lots/lotsUi';

/** Retours : les camions vides qui repartent chez leur propriétaire, à rendre, en retour, rendus. */
const LotsRetoursPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { peutEcrire } = useDroitsLots();
  const { options: societes } = useTransporteursLots();

  const brut = (searchParams.get('retour') || '').toUpperCase();
  const onglet = RETOURS_LOT.includes(brut) ? brut : RETOURS_LOT[0];
  const transporteur = (searchParams.get('transporter_id') || '').trim();
  const q = (searchParams.get('q') || '').trim();

  const [recherche, setRecherche] = useState(q);
  const rechercheDifferee = useDebouncedValue(recherche);
  const [lignes, setLignes] = useState([]);
  const [total, setTotal] = useState(0);
  const [compteurs, setCompteurs] = useState({});
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [coches, setCoches] = useState(() => new Set());
  const [dialogue, setDialogue] = useState(null);
  const [resultat, setResultat] = useState(null);

  const changer = useCallback(
    (changements) =>
      setSearchParams(
        (precedent) => {
          const suivant = new URLSearchParams(precedent);
          Object.entries(changements).forEach(([cle, valeur]) => {
            if (!valeur) suivant.delete(cle);
            else suivant.set(cle, valeur);
          });
          return suivant;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  useEffect(() => {
    if (rechercheDifferee.trim() === q) return;
    changer({ q: rechercheDifferee.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheDifferee]);

  const parametres = useMemo(() => {
    const p = { return_status: onglet };
    if (transporteur) p.transporter_id = transporteur;
    if (q) p.q = q;
    return p;
  }, [onglet, transporteur, q]);
  const cleFiltres = JSON.stringify(parametres);

  const pagination = usePaginationServeur(total, { reinitialiser: cleFiltres });
  const { page, limit: taille } = pagination;

  const nouvelleDemande = useDerniereDemande();
  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      const perimee = nouvelleDemande();
      if (!silencieux) setChargement(true);
      try {
        const [resultatListe, tableau] = await Promise.all([
          lotsAPI.trucks({ ...parametres, page, page_size: taille }),
          lotsAPI.dashboard().catch(() => null),
        ]);
        if (perimee()) return;
        setLignes(resultatListe.items);
        setTotal(resultatListe.total);
        if (tableau?.totals?.returns) setCompteurs(tableau.totals.returns);
        setErreur(false);
      } catch (err) {
        if (perimee()) return;
        console.error('Failed to load lot returns:', err);
        setErreur(true);
        toast.error(messageErreur(err, 'Impossible de charger les retours'));
      } finally {
        if (!perimee()) setChargement(false);
      }
    },
    [parametres, page, taille, nouvelleDemande],
  );

  useEffect(() => {
    charger();
  }, [charger]);

  useRafraichirSur(['lots', 'trajets'], charger);

  // Une autre liste (onglet, filtre, page) : les cases cochées ne valent plus.
  useEffect(() => {
    setCoches(new Set());
  }, [cleFiltres, page, taille]);

  const optionsTransporteurs = useMemo(
    () => [{ value: '', label: 'Tous les transporteurs' }, ...societes],
    [societes],
  );

  const cochees = useMemo(() => lignes.filter((ligne) => coches.has(ligne.id)), [lignes, coches]);
  const aPartir = useMemo(() => cochees.filter(peutPartirEnRetour), [cochees]);
  const aRendre = useMemo(() => cochees.filter(peutEtreRendu), [cochees]);
  const toutEstCoche = lignes.length > 0 && lignes.every((ligne) => coches.has(ligne.id));

  const basculer = (identifiant) =>
    setCoches((avant) => {
      const suite = new Set(avant);
      if (suite.has(identifiant)) suite.delete(identifiant);
      else suite.add(identifiant);
      return suite;
    });
  const toutCocher = (valeur) => setCoches(valeur ? new Set(lignes.map((ligne) => ligne.id)) : new Set());

  const surRetour = (reponse) => {
    setResultat(resumerRetour(reponse, indexerLignes(lignes)));
    setCoches(new Set());
    charger({ silencieux: true });
  };

  const filtresActifs = [transporteur, q].filter(Boolean).length;
  const effacer = () => {
    setRecherche('');
    changer({ transporter_id: '', q: '' });
  };

  const elementsImpression = [
    {
      label: `Camions : ${libelle('lot_retour', onglet).toLowerCase()}`,
      filename: 'retours-des-camions',
      url: IMPRESSION_SUIVI_LOTS,
      formats: ['pdf', 'xlsx', 'csv'],
      params: parametres,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="lots-retours">
      <PageHeader
        eyebrow="Lots attribués"
        title="Retours"
        stats={RETOURS_LOT.map((code) => ({
          label: libelle('lot_retour', code),
          valeur: Number(compteurs[code]) || 0,
          ton: code === 'A_RENDRE' && Number(compteurs[code]) > 0 ? 'attention' : undefined,
          onClick: () => changer({ retour: code === RETOURS_LOT[0] ? '' : code }),
          testid: `retours-stat-${code}`,
        }))}
        actions={
          <>
            <PrintMenu items={elementsImpression} label="Imprimer" />
            {peutEcrire ? <BoutonEnvoiResponsable params={parametres} className="!max-w-none" testid="retours-send-manager" /> : null}
          </>
        }
      />

      {/* Onglets, recherche étroite et filtres à sa droite. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="retours-filtres">
        <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5" role="group" aria-label="État du retour">
          {RETOURS_LOT.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => changer({ retour: code === RETOURS_LOT[0] ? '' : code })}
              aria-pressed={onglet === code}
              className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold transition-colors',
                onglet === code ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`retours-onglet-${code}`}
            >
              <span className={cn('h-2 w-2 rounded-full', COULEURS_RETOUR[code])} aria-hidden="true" />
              {libelle('lot_retour', code)}
              <span className="tabular-nums">{Number(compteurs[code]) || 0}</span>
            </button>
          ))}
        </div>
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Plaque, chauffeur…"
          label="Rechercher un camion"
          testid="retours-recherche"
        />
        <SearchableSelect
          className="input-base w-52"
          data-testid="retours-filtre-transporteur"
          aria-label="Transporteur"
          placeholder="Transporteur"
          searchPlaceholder="Rechercher un transporteur…"
          emptyMessage="Aucun transporteur trouvé"
          value={transporteur}
          onValueChange={(valeur) => changer({ transporter_id: valeur })}
          options={optionsTransporteurs}
        />
        {filtresActifs > 0 ? (
          <Button variant="ghost" size="sm" onClick={effacer} data-testid="retours-effacer-filtres">
            <X className="h-4 w-4" aria-hidden="true" />
            Effacer les filtres
          </Button>
        ) : null}
      </div>

      <PanneauResultat resume={resultat} onFermer={() => setResultat(null)} />

      {peutEcrire && cochees.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2"
          data-testid="retours-barre-selection"
        >
          <span className="text-sm font-medium">
            {cochees.length} camion{cochees.length > 1 ? 's' : ''} coché{cochees.length > 1 ? 's' : ''}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDialogue(ACTION_DEPART_RETOUR)}
            disabled={aPartir.length === 0}
            data-testid="retours-depart"
          >
            <Undo2 className="h-4 w-4" aria-hidden="true" />
            Départ retour ({aPartir.length})
          </Button>
          <Button
            size="sm"
            onClick={() => setDialogue(ACTION_RENDU)}
            disabled={aRendre.length === 0}
            data-testid="retours-rendu"
          >
            Rendu ({aRendre.length})
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCoches(new Set())}>
            Tout décocher
          </Button>
        </div>
      ) : null}

      {chargement && lignes.length === 0 ? (
        <LoadingState shape="table" />
      ) : erreur && lignes.length === 0 ? (
        <EmptyState
          variant="error"
          title="Les retours n’ont pas pu être chargés"
          description="Réessayez dans un instant."
          actionLabel="Réessayer"
          onAction={() => charger()}
        />
      ) : lignes.length === 0 ? (
        <EmptyState
          variant={filtresActifs ? 'filtered' : 'empty'}
          icon={Undo2}
          title={filtresActifs ? 'Aucun résultat' : 'Aucun camion dans cette liste'}
          description={filtresActifs ? 'Aucun camion ne correspond aux filtres actifs.' : undefined}
        />
      ) : (
        <Card className="card-base">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  {peutEcrire ? (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={toutEstCoche}
                        onCheckedChange={(valeur) => toutCocher(Boolean(valeur))}
                        aria-label="Cocher tous les camions de la page"
                        data-testid="retours-cocher-tout"
                      />
                    </TableHead>
                  ) : null}
                  <TableHead>Lot</TableHead>
                  <TableHead>Tracteur</TableHead>
                  <TableHead>Citerne</TableHead>
                  <TableHead>Chauffeur</TableHead>
                  <TableHead>Transporteur</TableHead>
                  <TableHead>Départ retour</TableHead>
                  <TableHead>Rendu le</TableHead>
                  <TableHead>Trajet</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lignes.map((ligne) => (
                  <TableRow
                    key={ligne.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/lots/${ligne.lot_id}`)}
                    data-state={coches.has(ligne.id) ? 'selected' : undefined}
                    data-testid={`retours-ligne-${ligne.id}`}
                  >
                    {peutEcrire ? (
                      <TableCell className="w-10" onClick={(evenement) => evenement.stopPropagation()}>
                        <Checkbox
                          checked={coches.has(ligne.id)}
                          onCheckedChange={() => basculer(ligne.id)}
                          aria-label={`Cocher le camion ${ligne.tractor_plate}`}
                          data-testid={`retours-cocher-${ligne.id}`}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="whitespace-nowrap">
                      <LienLot lotId={ligne.lot_id} reference={ligne.lot_reference} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono font-semibold">{ligne.tractor_plate}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono">{ligne.tank_plate || '—'}</TableCell>
                    <TableCell>
                      <ChauffeurLigne ligne={ligne} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{ligne.transporter_name || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(ligne.return_started_at)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(ligne.returned_at)}</TableCell>
                    <TableCell>
                      <LienTrajet tripId={ligne.trip_id} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {lignes.length > 0 ? <BarrePagination pagination={pagination} nom="camions" /> : null}

      {peutEcrire ? (
        <RetourDialog
          open={Boolean(dialogue)}
          onOpenChange={(ouvert) => !ouvert && setDialogue(null)}
          action={dialogue || ACTION_DEPART_RETOUR}
          lineIds={(dialogue === ACTION_RENDU ? aRendre : aPartir).map((ligne) => ligne.id)}
          onDone={surRetour}
        />
      ) : null}
    </div>
  );
};

export default LotsRetoursPage;
